import { Button, Grid, SvgIcon, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { baseApiUrl } from 'src/config';
import { subscribeSubmissionevent } from 'src/services/apiServices/constants';
import { viewFormByFormID } from 'src/utils/jotform-functions';
import loader from 'src/components/LoaderImg/loader.gif';
import { Link } from 'react-router-dom';
import {
  errorForLoaderDocumentation,
  pullUuidAction,
  requestForLoaderDocumentation,
  resetForLoaderDocumentation,
  resetUuidAction,
  successForLoaderDocumentation,
} from 'src/redux/actions/documentationAction';
import { getAccessToken } from 'src/routes/AuthContext';
import { getTherapistCred } from 'src/utils/get-therapist-credential';
import { getSelectedFacilityName } from 'src/utils/get-selected-facility-name';
import useCurrentUser from 'src/hooks/use-current-user';

function ViewAddNewScreenForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const getPatientId = useSelector((state: any) => state?.getPatientIdReducer);
  const { document } = location?.state || { document: '' };
  const submissionSse = useRef<EventSource | null>(null);
  const pullUuidResponse = useSelector((state: any) => state.pullUuidRducder);
  const [credentials, currentRoles, licenseNumber, discipline] = useCurrentUser([
    'credentials',
    'currentRoles',
    'licenseNumber',
    'discipline',
  ]);
  const isDocLoader = useSelector((state: any) => state.loaderDocumenattionReducer);
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const getCredentials = typeof credentials === 'string' ? credentials?.replaceAll(' ', '') : '';
  const allFacilities = useSelector(
    (state: any) => state?.getFacilityDropDownReducer?.response?.data?.data
  );
  const [iframeRef, setIframeRef] = useState<any | null>(null);
  const siteOfService = getSelectedFacilityName(allFacilities).replace(/%20/g, ' ');

  const updateIFrameRef = useCallback((node: any) => {
    setIframeRef(node);
  }, []);

  useEffect(() => {
    const iframeElement = iframeRef;

    if (iframeElement) {
      const interval = setInterval(async () => {
        const response = await getAccessToken();
      }, 15000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [iframeRef]);

  const backToEOCDetails = () => {
    navigate('/patients/patient-details/screening');
    dispatch(resetUuidAction());
    dispatch(resetForLoaderDocumentation());
  };

  const getHiddenUuid = () => {
    return `${pullUuidResponse.response?.trim()}X${
      window.location.hostname === 'localhost'
        ? localStorage.getItem('realm')
        : window.location.hostname.split('-')[0]
    }`;
  };

  useEffect(() => {
    if (!document) return;
    dispatch(
      pullUuidAction({
        screeningId: null,
        patientId: getPatientId,
        entityType: 'SCREENING',
        formId: document?.formId,
        isCompleted: false,
      })
    );
  }, [document]);

  useEffect(() => {
    if (!pullUuidResponse.response) return;

    const createEventSource = () => {
      submissionSse.current = new EventSource(
        `${baseApiUrl}${subscribeSubmissionevent}/${pullUuidResponse.response}`
      );

      submissionSse.current.addEventListener('message', (e) => {
        const eventData = JSON.parse(e.data);
        switch (eventData?.data) {
          case '1':
            dispatch(requestForLoaderDocumentation(eventData?.data));
            break;
          case '2':
            dispatch(successForLoaderDocumentation(eventData?.data));
            break;
          case '3':
            dispatch(errorForLoaderDocumentation(eventData?.error));
            break;
          default:
            break;
        }
      });

      submissionSse.current.addEventListener('error', (e: any) => {
        if (e.eventPhase === 2) {
          if (submissionSse.current) {
            submissionSse.current.close();
          }
          createEventSource();
        }
      });

      return () => {
        if (submissionSse.current) {
          submissionSse.current.close();
        }
      };
    };

    createEventSource();

    return () => {
      if (submissionSse.current) {
        submissionSse.current.close();
      }
    };
  }, [pullUuidResponse]);

  useEffect(() => {
    if (!isDocLoader.loader && isDocLoader.response === null) return;
    if (isDocLoader.error) {
      console.error(isDocLoader?.response);
      return;
    }
    if (!isDocLoader.loader) {
      dispatch(resetForLoaderDocumentation());
      dispatch(resetUuidAction());
      navigate('/patients/patient-details/screening');
    }
  }, [isDocLoader]);

  return (
    <>
      <Stack
        gap="1.5rem"
        sx={{ padding: '0 4rem' }}
      >
        <Button
          sx={{
            justifyContent: 'flex-start',
            color: '#111927',
            width: 'max-content',
            padding: '0px',
          }}
          onClick={() => backToEOCDetails}
          startIcon={
            <SvgIcon>
              <ArrowBackIcon sx={{ color: '#6C737F' }} />
            </SvgIcon>
          }
        >
          <Link
            style={{ color: '#111927' }}
            to={'/patients/patient-details/screening'}
          >
            <Typography variant="subtitle1">Back to Screening List</Typography>
          </Link>
        </Button>
        <Stack
          flexDirection="row"
          gap="1rem"
          alignItems="center"
        >
          <Typography variant="h5">Create Screening</Typography>
        </Stack>
      </Stack>

      {isDocLoader.loader ? (
        <img
          src={loader}
          alt="loader"
          style={{ width: '10%', margin: 'auto' }}
        />
      ) : (
        <Box
          sx={{ height: '100%' }}
          p={3}
        >
          {pullUuidResponse.response && (
            <iframe
              src={`${viewFormByFormID(
                document?.formId
              )}?hiddenUuid=${getHiddenUuid()}&licenseNumber=${
                licenseNumber || ''
              }&patientName[first]=${patientData?.firstName}&patientName[last]=${patientData?.lastName}&text20=${
                licenseNumber || ''
              }&credentials=${
                getCredentials || getTherapistCred(discipline, currentRoles)
              }&siteOfService=${siteOfService}&siteOf=${siteOfService}`}
              ref={updateIFrameRef}
              width={'100%'}
              height={'100%'}
              title="This is an iframe"
              style={{ border: 'none' }}
            />
          )}
        </Box>
      )}
    </>
  );
}

export default ViewAddNewScreenForm;
